import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useKey } from 'react-use';
import styled, { CSSProperties, keyframes } from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullScreen } from '@sorare/core/src/atoms/layout/FullScreen';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { getRgbColorFromHex } from '@sorare/core/src/lib/colors';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DefaultPosition } from 'assets/icons/DefaultPosition';
import { Glove } from 'assets/icons/Glove';
import { Jersey } from 'assets/icons/Jersey';
import { Shield } from 'assets/icons/Shield';
import { Shoe } from 'assets/icons/Shoe';

import { LineupSetAnimation_so5Leaderboard } from './__generated__/index.graphql';
import lineupSetSound from './assets/lineup-set-sound.mp3';

const leagueIconAnim = keyframes`
  from {
    opacity: 0;
  }
  to {
   opacity: 1;
  }
`;

const wrapperAnim = keyframes`
  0% {
    opacity: 0;
  }
  12% {
   opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const LeagueIcon = styled.img`
  opacity: 0;
  animation: ${leagueIconAnim} 0.4s 0.6s linear forwards;
`;
const Wrapper = styled(FullScreen)`
  isolation: isolate;
  background: linear-gradient(
    180deg,
    rgba(var(--bg-color), 0.3) 0%,
    rgba(var(--bg-color), 0) 100%
  );
  opacity: 0;
  backdrop-filter: blur(12px);
  animation: ${wrapperAnim} 2.2s 0.5s linear forwards;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  & > * {
    flex: unset;
  }
`;

const divisionIconAnimation = keyframes`
  from {
    opacity: 0;
    scale: 1.5;
  }
  to {
   opacity: 1;
   scale: 1;
  }
`;
const DivisionIcon = styled.img`
  margin-top: -32px;
  filter: drop-shadow(0px 0px 24px rgba(0, 0, 0, 0.7));
  opacity: 0;
  animation: ${divisionIconAnimation} 0.3s 1s
    cubic-bezier(0.07, 0.35, 0.57, 0.93) forwards;
`;

const CenterContent = styled(Vertical)`
  flex: 3;
`;

const titleAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
   opacity: 1;
   transform: translateY(0px);
  }
`;

const Title = styled.h1`
  font-size: 90px;
  text-align: center;
  text-transform: uppercase;
  mix-blend-mode: overlay;
  & > * {
    animation-name: ${titleAnimation};
    animation-duration: 0.4s;
    animation-fill-mode: forwards;
    animation-timing-function: cubic-bezier(0.07, 0.35, 0.57, 0.93);
  }
`;

const FirstLine = styled.div`
  opacity: 0;
  animation-delay: 1s;
`;

const SecondLine = styled.div`
  margin-top: -20px;
  opacity: 0;
  animation-delay: 1.2s;
  animation-duration: 0.3s;
`;

const Positions = styled(SBHorizontal)`
  flex: 1;
  width: 60%;
  @media ${tabletAndAbove} {
    width: 30%;
  }
  & > * {
    opacity: 0;
    animation-name: ${titleAnimation};
    animation-duration: 0.3s;
    animation-fill-mode: forwards;
    animation-timing-function: cubic-bezier(0.07, 0.35, 0.57, 0.93);
    animation-delay: calc(var(--position-icon-index) * 0.07s + 1s);
  }
`;

type Props = {
  so5Leaderboard: LineupSetAnimation_so5Leaderboard;
  onNext: () => void;
};

const positionIcons = [Glove, Shield, Jersey, Shoe, DefaultPosition] as const;

export const LineupSetAnimation = ({ so5Leaderboard, onNext }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  useKey('Escape', onNext);

  const { sport } = useSportContext();
  return (
    <Wrapper
      style={
        {
          '--bg-color': getRgbColorFromHex(so5Leaderboard.so5League.color),
        } as CSSProperties
      }
      onAnimationEnd={({ target, currentTarget }) => {
        if (target === currentTarget) {
          onNext();
        }
      }}
    >
      <CenterContent center>
        <LeagueIcon src={so5Leaderboard.so5League.logoUrl} alt="" width={200} />
        <Title>
          <FormattedMessage
            id="LineupSetAnimation.title"
            defaultMessage="<l1>Lineup</l1><l2>Set</l2>"
            values={{
              l1: chunks => <FirstLine>{chunks}</FirstLine>,
              l2: chunks => <SecondLine>{chunks}</SecondLine>,
            }}
          />
        </Title>
        {sport === Sport.FOOTBALL && (
          <DivisionIcon
            src={so5Leaderboard.divisionIconUrl}
            alt=""
            width={128}
            height={128}
          />
        )}
      </CenterContent>
      {sport === Sport.FOOTBALL && (
        <Positions center>
          {positionIcons.map((Icon, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              style={
                {
                  '--position-icon-index': index,
                } as CSSProperties
              }
            >
              <Icon size={isTabletAndAbove ? 6 : 4} />
            </div>
          ))}
        </Positions>
      )}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio src={lineupSetSound} autoPlay />
    </Wrapper>
  );
};

LineupSetAnimation.fragments = {
  so5Leaderboard: gql`
    fragment LineupSetAnimation_so5Leaderboard on So5Leaderboard {
      slug
      iconUrl
      divisionIconUrl
      so5League {
        slug
        color
        logoUrl
      }
    }
  ` as TypedDocumentNode<LineupSetAnimation_so5Leaderboard>,
};
